import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { KIND_META, fmtQty, fmtSigned, fmtMoney } from "./ledgerConstants";

const useStyles = makeStyles((theme) => ({
  hero: {
    background: "linear-gradient(135deg,#fff7ed 0%,#ffffff 70%)",
    border: "1px solid #fed7aa",
    borderRadius: 12,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: 8,
    flexShrink: 0,
  },
  top: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" },
  name: { fontSize: "1.05rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 },
  sub: { fontSize: "0.72rem", color: "#64748b", marginTop: 2 },
  avail: { textAlign: "right" },
  availLabel: { fontSize: "0.6rem", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "#94a3b8" },
  availVal: { fontSize: "1.6rem", fontWeight: 800, lineHeight: 1.1 },
  flow: { display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 6, marginTop: 10 },
  tile: {
    flex: "1 1 92px",
    minWidth: 92,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderTop: "3px solid #cbd5e1",
    borderRadius: 8,
    padding: "5px 9px",
  },
  tileLabel: { fontSize: "0.6rem", fontWeight: 800, letterSpacing: ".05em", textTransform: "uppercase", color: "#94a3b8" },
  tileVal: { fontSize: "1rem", fontWeight: 800, lineHeight: 1.25 },
  tileSub: { fontSize: "0.62rem", color: "#64748b" },
  values: { display: "flex", flexWrap: "wrap", gap: 14, marginTop: 8, fontSize: "0.74rem", color: "#475569" },
  day: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "flex",
    justifyContent: "space-between",
    padding: "5px 12px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  ev: {
    display: "flex",
    gap: 10,
    padding: "8px 12px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    "&:hover": { background: "#fffaf5" },
  },
  rail: { display: "flex", flexDirection: "column", alignItems: "center", width: 14, flexShrink: 0 },
  dot: { width: 10, height: 10, borderRadius: "50%", marginTop: 4, flexShrink: 0 },
  line: { flex: 1, width: 2, background: "#e2e8f0", marginTop: 2 },
  evMain: { flex: 1, minWidth: 0 },
  evRow: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" },
  evTitle: { fontSize: "0.82rem", fontWeight: 700, color: "#0f172a" },
  evSub: { fontSize: "0.7rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  plus: { color: "#15803d", fontWeight: 800 },
  minus: { color: "#dc2626", fontWeight: 800 },
  bal: { fontSize: "0.68rem", color: "#64748b", whiteSpace: "nowrap" },
  time: { fontSize: "0.66rem", color: "#94a3b8", marginRight: 6 },
}));

const DOC_PREFIX = { invoice: "Invoice", purchase: "Purchase", adjustment: "Adjustment", return: "Return" };

// Hero card for a selected item: identity, available stock and the balance
// "flow" for the chosen date range (opening -> movements -> closing).
export function ItemHero({ item, symbol }) {
  const c = useStyles();
  const k = item.byKind || {};
  const q = (key) => k[key]?.qty || 0;
  const n = (key) => k[key]?.count || 0;
  const other = q("RETURN") + q("REVERSAL") + q("OTHER");
  const otherCount = n("RETURN") + n("REVERSAL") + n("OTHER");
  const addIn = q("ADJUST_ADD") + q("OPENING");
  const addCount = n("ADJUST_ADD") + n("OPENING");
  const tiles = [
    { label: "Opening", val: item.openingBalance, color: "#475569", top: "#94a3b8", sub: "before range" },
    n("PURCHASE")
      ? { label: "Purchased", val: q("PURCHASE"), signed: true, color: "#15803d", top: "#22c55e", sub: `${n("PURCHASE")} entries` }
      : null,
    addCount
      ? { label: "Added / Opening", val: addIn, signed: true, color: "#0f766e", top: "#14b8a6", sub: `${addCount} entries` }
      : null,
    n("SALE")
      ? { label: "Sold", val: q("SALE"), signed: true, color: "#dc2626", top: "#f97316", sub: `${n("SALE")} entries` }
      : null,
    n("ADJUST_LESS")
      ? { label: "Deducted", val: q("ADJUST_LESS"), signed: true, color: "#b45309", top: "#f59e0b", sub: `${n("ADJUST_LESS")} entries` }
      : null,
    otherCount
      ? { label: "Returns / Reversals", val: other, signed: true, color: "#7e22ce", top: "#a855f7", sub: `${otherCount} entries` }
      : null,
    { label: "Closing", val: item.closingBalance, color: "#0f172a", top: "#f97316", sub: "end of range" },
  ].filter(Boolean);

  return (
    <div className={c.hero}>
      <div className={c.top}>
        <div>
          <div className={c.name}>{item.name}</div>
          <div className={c.sub}>
            {[item.category, item.barcode && `Barcode ${item.barcode}`, `${fmtQty(item.rowCount)} ledger entries all-time`]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
        <div className={c.avail}>
          <div className={c.availLabel}>Available now</div>
          <div className={c.availVal} style={{ color: item.available < 0 ? "#dc2626" : "#15803d" }}>
            {fmtQty(item.available)} <span style={{ fontSize: "0.8rem", color: "#64748b" }}>{item.stockUnit}</span>
          </div>
        </div>
      </div>
      <div className={c.flow}>
        {tiles.map((t) => (
          <div key={t.label} className={c.tile} style={{ borderTopColor: t.top }}>
            <div className={c.tileLabel}>{t.label}</div>
            <div className={c.tileVal} style={{ color: t.color }}>
              {t.signed ? fmtSigned(t.val) : fmtQty(t.val)} <span className={c.tileSub}>{item.stockUnit}</span>
            </div>
            <div className={c.tileSub}>{t.sub}</div>
          </div>
        ))}
      </div>
      {(item.sales?.docs > 0 || item.purchases?.docs > 0) && (
        <div className={c.values}>
          {item.sales?.docs > 0 && (
            <span>
              Sales value <b style={{ color: "#c2410c" }}>{fmtMoney(item.sales.amount, symbol)}</b> · {item.sales.docs} invoice
              {item.sales.docs > 1 ? "s" : ""}
            </span>
          )}
          {item.purchases?.docs > 0 && (
            <span>
              Purchase cost <b style={{ color: "#15803d" }}>{fmtMoney(item.purchases.amount, symbol)}</b> · {item.purchases.docs} purchase
              {item.purchases.docs > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Vertical, day-grouped timeline of one item's movements with running balance.
export function ItemTimeline({ rows, symbol, fmtDay, fmtTime, onSelect }) {
  const c = useStyles();
  const groups = [];
  rows.forEach((r) => {
    const day = fmtDay(r.date);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.rows.push(r);
    else groups.push({ day, rows: [r] });
  });
  return (
    <div>
      {groups.map((g) => {
        const net = g.rows.reduce((a, r) => a + (r.effectStock || 0), 0);
        return (
          <div key={g.day}>
            <div className={c.day}>
              <span>{g.day}</span>
              <span style={{ color: net >= 0 ? "#15803d" : "#dc2626" }}>Net {fmtSigned(net)}</span>
            </div>
            {g.rows.map((r) => {
              const m = KIND_META[r.kind] || KIND_META.OTHER;
              const title = r.doc
                ? `${m.label} · ${DOC_PREFIX[r.doc.type] || "Doc"} #${r.doc.number}`
                : r.kind === "OPENING"
                ? "Opening stock"
                : m.label;
              const detail = [
                r.doc?.party,
                r.line && `${fmtQty(r.line.qty)} × ${fmtMoney(r.line.rate, symbol)} = ${fmtMoney(r.line.amount, symbol)}`,
                !r.doc && r.note,
                r.user && `by ${r.user}`,
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <div key={r.id} className={c.ev} onClick={() => onSelect(r)}>
                  <div className={c.rail}>
                    <span className={c.dot} style={{ background: m.color }} />
                    <span className={c.line} />
                  </div>
                  <div className={c.evMain}>
                    <div className={c.evRow}>
                      <span className={c.evTitle}>
                        {title}
                        {r.doc?.deleted && <span style={{ color: "#dc2626", fontSize: "0.62rem", marginLeft: 6 }}>DELETED</span>}
                      </span>
                      <span className={r.effect >= 0 ? c.plus : c.minus} style={{ whiteSpace: "nowrap" }}>
                        {fmtSigned(r.effect)} {r.unit}
                      </span>
                    </div>
                    <div className={c.evRow}>
                      <span className={c.evSub}>
                        <span className={c.time}>{fmtTime(r.date)}</span>
                        {detail}
                      </span>
                      <span className={c.bal}>
                        Bal <b style={{ color: "#0f172a" }}>{r.balance != null ? fmtQty(r.balance) : "-"}</b>
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
